import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    Image,
    SafeAreaView,
    Platform,
    ActivityIndicator,
    TextInput,
    Alert
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getMyOrders, cancelOrder, confirmOrderReceipt } from '../../api/marketplace';

const OrderDetailScreen = ({ route, navigation }) => {
    const { orderId } = route.params;
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [rating, setRating] = useState(5);
    const [feedback, setFeedback] = useState('');

    useEffect(() => {
        fetchOrderDetails();
    }, [orderId]);

    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            const orders = await getMyOrders();
            const foundOrder = orders.find(o => o.id === orderId);
            setOrder(foundOrder);
        } catch (error) {
            console.error('Fetch Order Detail Error:', error);
            Alert.alert('Error', 'Unable to fetch order details');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelOrder = () => {
        console.log('DEBUG: handleCancelOrder triggered for ID:', order?.id);

        const performCancel = async () => {
            console.log('DEBUG: Starting cancellation process for:', order.id);
            try {
                setCancelling(true);
                const result = await cancelOrder(order.id);
                console.log('DEBUG: API response:', result);
                Alert.alert("Success", "Order cancelled successfully.");
                navigation.goBack();
            } catch (error) {
                console.error('DEBUG: Cancellation failed error:', error);
                Alert.alert("Error", error?.message || error || "Failed to cancel order");
            } finally {
                setCancelling(false);
            }
        };

        if (Platform.OS === 'web') {
            if (window.confirm("Are you sure you want to cancel this order?")) {
                performCancel();
            } else {
                console.log('DEBUG: User cancelled the confirm dialog on web');
            }
        } else {
            Alert.alert(
                "Cancel Order",
                "Are you sure you want to cancel this order? This action cannot be undone.",
                [
                    { text: "No", style: "cancel", onPress: () => console.log('DEBUG: User clicked No') },
                    {
                        text: "Yes, Cancel",
                        style: "destructive",
                        onPress: performCancel
                    }
                ]
            );
        }
    };

    const handleConfirmReceipt = async () => {
        try {
            setConfirming(true);
            await confirmOrderReceipt(order.id, rating, feedback);
            Alert.alert("Success", "Order completed and payment released to vendor.");
            fetchOrderDetails();
        } catch (error) {
            Alert.alert("Error", error?.message || error || "Failed to confirm receipt");
        } finally {
            setConfirming(false);
        }
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#6366F1" />
            </View>
        );
    }

    if (!order) {
        return (
            <View style={styles.errorContainer}>
                <Text>Order not found</Text>
                <TouchableOpacity onPress={() => navigation.goBack()}>
                    <Text style={styles.backButtonText}>Go Back</Text>
                </TouchableOpacity>
            </View>
        );
    }

    const getStatusSteps = (status) => {
        const steps = [
            { id: 'placed', label: 'Order Placed', date: order.createdAt, description: 'Your order has been received' },
            { id: 'paid', label: 'Payment Confirmed', date: order.status !== 'pending_payment' ? order.updatedAt : null, description: 'Payment successful' },
            { id: 'processing', label: 'Processing', date: ['paid_processing', 'dispatched', 'delivered', 'completed'].includes(status) ? order.updatedAt : null, description: 'Vendor is preparing your order' },
            { id: 'dispatched', label: 'Dispatched', date: ['dispatched', 'delivered', 'completed'].includes(status) ? order.updatedAt : null, description: 'Order is on the way' },
            { id: 'delivered', label: 'Delivered', date: ['delivered', 'completed'].includes(status) ? order.updatedAt : null, description: 'Enjoy your purchase!' },
        ];

        let activeStepIndex = 0;
        if (status === 'pending_payment') activeStepIndex = 0;
        else if (status === 'paid_processing') activeStepIndex = 2;
        else if (status === 'dispatched') activeStepIndex = 3;
        else if (['delivered', 'completed'].includes(status)) activeStepIndex = 4;
        else activeStepIndex = 1; // paid but not yet processing

        return { steps, activeStepIndex };
    };

    const { steps, activeStepIndex } = getStatusSteps(order.status);

    return (
        <SafeAreaView style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
                    <Ionicons name="arrow-back" size={24} color="#333" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Order Details</Text>
                <View style={{ width: 40 }} />
            </View>

            <ScrollView
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.scrollContent}
                style={{ flex: 1 }}
            >
                {/* Order Summary Header */}
                <View style={styles.summaryCard}>
                    <View style={styles.summaryRow}>
                        <Text style={styles.orderLabel}>Order #{order.id}</Text>
                        <Text style={styles.orderStatusLabel}>{order.status.toUpperCase().replace('_', ' ')}</Text>
                    </View>
                    <View style={styles.summaryRow}>
                        <Text style={styles.orderDate}>Placed on {new Date(order.createdAt).toLocaleDateString()}</Text>
                    </View>
                    <View style={styles.separator} />
                    <View style={styles.summaryRow}>
                        <Text style={styles.totalLabel}>Total Amount:</Text>
                        <Text style={styles.totalValue}>KES {Number(order.total_amount).toLocaleString()}</Text>
                    </View>
                </View>

                {/* Items Section */}
                <View style={styles.sectionHeader}>
                    <Text style={styles.sectionTitle}>Items in your order</Text>
                    <Text style={styles.itemCount}>{order.items.length} Items</Text>
                </View>

                <View style={styles.itemsList}>
                    {order.items.map((item, index) => (
                        <View key={index} style={[styles.itemRow, index !== order.items.length - 1 && styles.compactItemBorder]}>
                            <Image
                                source={{ uri: item.item?.images?.[0] || 'https://via.placeholder.com/100' }}
                                style={styles.itemImage}
                            />
                            <View style={styles.itemDetails}>
                                <Text style={styles.itemName} numberOfLines={2}>{item.item?.name}</Text>
                                <View style={styles.qtyPriceRow}>
                                    <Text style={styles.itemQty}>Qty: {item.quantity}</Text>
                                    <View style={styles.dot} />
                                    <Text style={styles.itemPrice}>KES {Number(item.price_at_purchase).toLocaleString()}</Text>
                                </View>
                            </View>
                            <Text style={styles.rowTotal}>KES {(item.price_at_purchase * item.quantity).toLocaleString()}</Text>
                        </View>
                    ))}
                </View>

                {/* Tracking Progress Section */}
                {order.status !== 'cancelled' && (
                    <>
                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionTitle}>Tracking Status</Text>
                        </View>

                        <View style={styles.trackingContainer}>
                            {steps.map((step, index) => {
                                const isCompleted = index <= activeStepIndex;
                                const isLast = index === steps.length - 1;

                                return (
                                    <View key={step.id} style={styles.stepWrapper}>
                                        <View style={styles.stepIndicatorContainer}>
                                            <View style={[
                                                styles.stepCircle,
                                                isCompleted ? styles.completedCircle : styles.pendingCircle
                                            ]}>
                                                {isCompleted ? (
                                                    <Ionicons name="checkmark" size={12} color="#FFF" />
                                                ) : (
                                                    <View style={styles.dot} />
                                                )}
                                            </View>
                                            {!isLast && (
                                                <View style={[
                                                    styles.stepLine,
                                                    index < activeStepIndex ? styles.completedLine : styles.pendingLine
                                                ]} />
                                            )}
                                        </View>
                                        <View style={styles.stepContent}>
                                            <View style={styles.stepHeaderRow}>
                                                <Text style={[
                                                    styles.stepLabel,
                                                    isCompleted ? styles.completedLabelText : styles.pendingLabelText
                                                ]}>{step.label}</Text>
                                                {step.date && (
                                                    <Text style={styles.stepDate}>{new Date(step.date).toLocaleDateString()}</Text>
                                                )}
                                            </View>
                                            <Text style={styles.stepDescription}>{step.description}</Text>
                                        </View>
                                    </View>
                                );
                            })}
                        </View>
                    </>
                )}

                {/* Payment & Shipping Details */}
                <View style={styles.detailsCard}>
                    <Text style={styles.detailsTitle}>Payment Method</Text>
                    <View style={styles.detailsRow}>
                        <Ionicons name="card-outline" size={20} color="#666" />
                        <Text style={styles.detailsValue}>Mobile Money (M-Pesa)</Text>
                    </View>

                    <View style={styles.separator} />

                    <Text style={styles.detailsTitle}>Delivery Information</Text>
                    <View style={styles.detailsRow}>
                        <Ionicons name="location-outline" size={20} color="#666" />
                        <Text style={styles.detailsValue}>
                            {typeof order.delivery_address === 'object'
                                ? Object.values(order.delivery_address).join(', ')
                                : order.delivery_address || 'Standard Delivery'}
                        </Text>
                    </View>
                    {order.contact_phone && (
                        <View style={styles.detailsRow}>
                            <Ionicons name="call-outline" size={20} color="#666" />
                            <Text style={styles.detailsValue}>{order.contact_phone}</Text>
                        </View>
                    )}
                </View>

                {/* Confirm Receipt & Review Section */}
                {order.status === 'delivered' && (
                    <View style={styles.reviewCard}>
                        <Text style={styles.reviewTitle}>Confirm Receipt & Review</Text>
                        <Text style={styles.reviewSubtitle}>Please rate your experience with this vendor</Text>

                        <View style={styles.ratingRow}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <TouchableOpacity key={star} onPress={() => setRating(star)}>
                                    <Ionicons
                                        name={star <= rating ? "star" : "star-outline"}
                                        size={32}
                                        color={star <= rating ? "#F59E0B" : "#D1D5DB"}
                                    />
                                </TouchableOpacity>
                            ))}
                        </View>

                        <TextInput
                            style={styles.feedbackInput}
                            placeholder="Write a short review (optional)..."
                            multiline
                            numberOfLines={3}
                            value={feedback}
                            onChangeText={setFeedback}
                        />

                        <TouchableOpacity
                            style={[styles.confirmButton, confirming && styles.disabledButton]}
                            onPress={handleConfirmReceipt}
                            disabled={confirming}
                        >
                            {confirming ? <ActivityIndicator color="#FFF" /> : (
                                <Text style={styles.confirmButtonText}>CONFIRM & RELEASE FUNDS</Text>
                            )}
                        </TouchableOpacity>
                        <Text style={styles.releaseInfo}>Funds will be released from escrow to the vendor's wallet.</Text>
                    </View>
                )}

                {order.status === 'completed' && (
                    <View style={styles.completedCard}>
                        <Ionicons name="checkmark-circle" size={40} color="#10B981" />
                        <Text style={styles.completedTitle}>Order Completed</Text>
                        <Text style={styles.completedSubtitle}>Funds released. Thank you for shopping!</Text>
                        {order.buyer_feedback && (
                            <View style={styles.myFeedbackBox}>
                                <Text style={styles.feedbackLabel}>Your Feedback:</Text>
                                <Text style={styles.feedbackText}>"{order.buyer_feedback}"</Text>
                                <View style={styles.miniRating}>
                                    {[1, 2, 3, 4, 5].map(s => (
                                        <Ionicons key={s} name="star" size={12} color={s <= (order.satisfaction_rating || 5) ? "#F59E0B" : "#E5E7EB"} />
                                    ))}
                                </View>
                            </View>
                        )}
                    </View>
                )}

                {order.status === 'pending_payment' && (
                    <TouchableOpacity
                        style={styles.cancelButton}
                        onPress={() => {
                            console.log('TOUCH_EVENT: Cancel button pressed');
                            handleCancelOrder();
                        }}
                        disabled={cancelling}
                        activeOpacity={0.7}
                        hitSlop={{ top: 15, bottom: 15, left: 15, right: 15 }}
                    >
                        {cancelling ? (
                            <ActivityIndicator size="small" color="#EF4444" />
                        ) : (
                            <Text style={styles.cancelButtonText}>Cancel Order</Text>
                        )}
                    </TouchableOpacity>
                )}

                <TouchableOpacity
                    style={styles.buyAgainButton}
                    onPress={() => navigation.navigate('Marketplace')}
                >
                    <Text style={styles.buyAgainText}>Browse Marketplace</Text>
                </TouchableOpacity>

                <View style={{ height: 40 }} />
            </ScrollView>
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F8F9FA',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: '#FFF',
        borderBottomWidth: 1,
        borderBottomColor: '#EEE',
        paddingTop: Platform.OS === 'android' ? 40 : 12,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#111',
    },
    backButton: {
        padding: 4,
    },
    scrollContent: {
        padding: 16,
    },
    summaryCard: {
        backgroundColor: '#FFF',
        borderRadius: 12,
        padding: 16,
        marginBottom: 20,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 3,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    orderLabel: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111',
    },
    orderDate: {
        fontSize: 13,
        color: '#666',
    },
    totalLabel: {
        fontSize: 14,
        color: '#444',
    },
    totalValue: {
        fontSize: 18,
        fontWeight: '800',
        color: '#6366F1',
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        marginTop: 8,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#333',
    },
    itemCount: {
        fontSize: 13,
        color: '#6366F1',
        fontWeight: '600',
    },
    itemsList: {
        backgroundColor: '#FFF',
        borderRadius: 12,
        paddingHorizontal: 12,
        marginBottom: 20,
    },
    itemRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
    },
    compactItemBorder: {
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
    },
    itemImage: {
        width: 50,
        height: 50,
        borderRadius: 8,
        backgroundColor: '#F3F4F6',
    },
    itemDetails: {
        flex: 1,
        marginLeft: 12,
    },
    itemName: {
        fontSize: 14,
        fontWeight: '600',
        color: '#111',
        marginBottom: 2,
    },
    qtyPriceRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    itemPrice: {
        fontSize: 13,
        color: '#666',
    },
    itemQty: {
        fontSize: 13,
        color: '#666',
    },
    rowTotal: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111',
    },
    trackingContainer: {
        backgroundColor: '#FFF',
        borderRadius: 12,
        padding: 20,
        marginBottom: 20,
    },
    stepWrapper: {
        flexDirection: 'row',
    },
    stepIndicatorContainer: {
        alignItems: 'center',
        width: 24,
    },
    stepCircle: {
        width: 20,
        height: 20,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
    },
    completedCircle: {
        backgroundColor: '#10B981',
    },
    pendingCircle: {
        backgroundColor: '#E5E7EB',
        borderWidth: 1,
        borderColor: '#D1D5DB',
    },
    dot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: '#D1D5DB',
    },
    stepLine: {
        width: 2,
        flex: 1,
        marginVertical: -2,
        zIndex: 1,
    },
    completedLine: {
        backgroundColor: '#10B981',
    },
    pendingLine: {
        backgroundColor: '#E5E7EB',
    },
    stepContent: {
        flex: 1,
        marginLeft: 16,
        paddingBottom: 25,
    },
    stepHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    stepLabel: {
        fontSize: 14,
        fontWeight: '700',
    },
    completedLabelText: {
        color: '#111',
    },
    pendingLabelText: {
        color: '#9CA3AF',
    },
    stepDate: {
        fontSize: 11,
        color: '#6B7280',
    },
    stepDescription: {
        fontSize: 12,
        color: '#6B7280',
        marginTop: 2,
    },
    detailsCard: {
        backgroundColor: '#FFF',
        borderRadius: 12,
        padding: 16,
        marginBottom: 20,
    },
    detailsTitle: {
        fontSize: 12,
        textTransform: 'uppercase',
        color: '#9CA3AF',
        fontWeight: '700',
        letterSpacing: 0.5,
        marginBottom: 10,
    },
    detailsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 12,
    },
    detailsValue: {
        fontSize: 14,
        color: '#333',
        marginLeft: 10,
        fontWeight: '500',
    },
    separator: {
        height: 1,
        backgroundColor: '#EEE',
        marginVertical: 15,
    },
    buyAgainButton: {
        backgroundColor: '#6366F1',
        borderRadius: 12,
        paddingVertical: 15,
        alignItems: 'center',
        shadowColor: '#6366F1',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 4,
    },
    buyAgainText: {
        color: '#FFF',
        fontSize: 16,
        fontWeight: '700',
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    orderStatusLabel: {
        fontSize: 12,
        fontWeight: '800',
        color: '#6366F1',
        backgroundColor: '#EEF2FF',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4,
        overflow: 'hidden',
    },
    cancelButton: {
        borderWidth: 1,
        borderColor: '#EF4444',
        borderRadius: 12,
        paddingVertical: 14,
        alignItems: 'center',
        marginBottom: 12,
    },
    cancelButtonText: {
        color: '#EF4444',
        fontSize: 16,
        fontWeight: '700',
    },
    backButtonText: {
        color: '#6366F1',
        marginTop: 10,
        fontWeight: '600',
    },
    reviewCard: {
        backgroundColor: '#FFF',
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    reviewTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111',
        marginBottom: 4,
    },
    reviewSubtitle: {
        fontSize: 13,
        color: '#6B7280',
        marginBottom: 20,
        textAlign: 'center',
    },
    ratingRow: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 20,
    },
    feedbackInput: {
        width: '100%',
        backgroundColor: '#F9FAFB',
        borderRadius: 12,
        padding: 12,
        fontSize: 14,
        color: '#111',
        textAlignVertical: 'top',
        marginBottom: 20,
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    confirmButton: {
        backgroundColor: '#10B981',
        width: '100%',
        paddingVertical: 16,
        borderRadius: 12,
        alignItems: 'center',
    },
    confirmButtonText: {
        color: '#FFF',
        fontWeight: '900',
        letterSpacing: 0.5,
    },
    releaseInfo: {
        fontSize: 11,
        color: '#9CA3AF',
        marginTop: 12,
        textAlign: 'center',
    },
    disabledButton: {
        opacity: 0.6,
    },
    completedCard: {
        backgroundColor: '#ECFDF5',
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#A7F3D0',
    },
    completedTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#065F46',
        marginTop: 12,
    },
    completedSubtitle: {
        fontSize: 14,
        color: '#059669',
        textAlign: 'center',
        marginTop: 4,
    },
    myFeedbackBox: {
        width: '100%',
        backgroundColor: 'rgba(255,255,255,0.5)',
        padding: 12,
        borderRadius: 12,
        marginTop: 20,
    },
    feedbackLabel: {
        fontSize: 11,
        fontWeight: '700',
        color: '#065F46',
        textTransform: 'uppercase',
    },
    feedbackText: {
        fontSize: 14,
        color: '#065F46',
        fontStyle: 'italic',
        marginTop: 4,
    },
    miniRating: {
        flexDirection: 'row',
        marginTop: 8,
        gap: 2,
    }
});

export default OrderDetailScreen;
